import type { ReactNode } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

type Props = {
  labelId?: string
  label?: string
  children: ReactNode
}

export const Table = ({ labelId, label, children }: Props) => {
  return (
    <TableWrapper>
      <StyledTable aria-labelledby={labelId} aria-label={label}>
        {children}
      </StyledTable>
    </TableWrapper>
  )
}

const TableWrapper = styled.div`
  overflow-y: auto;
`

const StyledTable = styled.table`
  border-collapse: separate;
  width: 100%;
`

export const Thead = styled.thead`
  > tr:first-of-type > th:first-of-type {
    position: sticky;
    left: 0;
    border-left: 1px solid ${theme.colors.tableBorder};
  }

  > tr:first-of-type > th {
    border-top: 1px solid ${theme.colors.tableBorder};
  }

  > tr > th {
    word-break: keep-all;

    &.AnalystCoverage{
      word-break: normal;
    }
    
  }

  tr > th:first-of-type {
    border-left: 1px solid ${theme.colors.tableBorder};
  }
`

export const Tbody = styled.tbody`
  > tr > th {
    border-left: 1px solid ${theme.colors.tableBorder};
  }

  > tr > th:first-of-type {
    position: sticky;
    left: 0;
  }

  > tr > td:last-of-type {
    border-right: 1px solid ${theme.colors.tableBorder};
  }

  @media screen and (max-width: 1047px) {
    > tr > th {
      min-width: 30vw;
    }
  }
`

export const TbodyWithTwoColumnHeadings = styled.tbody<{
  firstHeadWidth: string
}>`
  > tr > th:first-of-type {
    position: sticky;
    left: ${({ firstHeadWidth }) => firstHeadWidth};
  }

  > tr > th[rowspan] {
    border-left: 1px solid ${theme.colors.tableBorder};
    position: sticky;
    left: 0;
    min-width: ${({ firstHeadWidth }) => firstHeadWidth};
  }

  > tr > th[rowspan] + th {
    position: sticky;
    left: ${({ firstHeadWidth }) => firstHeadWidth};
  }

  @media screen and (max-width: 1047px) {
    > tr > th[rowspan] + th {
      min-width: 14vw;
      position: sticky;
    }
  }
  &.StickyItem{
    position: sticky;
    top: 0;
    z-index: 10; /* 必要に応じて調整 */
  }
`

export const TbodyWithThreeColumnHeadings = styled.tbody<{
  firstHeadWidth: string
  secondHeadWidth?: string
}>`

  > tr > th:nth-last-of-type(1) {
      min-width:85px;
      position: sticky;
      left: calc(${({firstHeadWidth}) => firstHeadWidth} + ${({secondHeadWidth}) => secondHeadWidth || '0px'});
  }

  > tr > th:nth-last-of-type(2) {
      width: ${({secondHeadWidth}) => secondHeadWidth || '60px'};
      min-width: ${({secondHeadWidth}) => secondHeadWidth || '60px'};
      position: sticky;
      left: ${({firstHeadWidth}) => firstHeadWidth};
      border-left: 1px solid ${theme.colors.tableBorder};
  }

  > tr > th:nth-last-of-type(3) {
      width: ${({firstHeadWidth}) => firstHeadWidth};
      min-width:${({firstHeadWidth}) => firstHeadWidth};
      position: sticky;
      left: 0px;
      border-left: 1px solid ${theme.colors.tableBorder};
  }

  @media screen and (max-width: 1047px) {
    > tr > th:nth-last-of-type(1) {
      min-width: 14vw;
      position: sticky; 
    }
  }
  &.StickyItem{
    position: sticky;
    top: 0;
    z-index: 10; /* 必要に応じて調整 */
  }
`

export const Tr = styled.tr``
  
export type Color =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'darkest'
  | 'accentYellow'
  | 'holiday'
  | 'tertiary'
  | 'quaternary'
type TextAlign = 'center' | 'left' | 'right'

export const Th = styled.th<{
  color?: Color
  textAlign?: TextAlign
  noBorder?: boolean
  firstHeadWidth?: string
  isBold?: boolean 
}>`
  padding: 13px 5px 15px 5px;
  font-size: 16px;
  text-align: ${({ textAlign }) => textAlign ?? 'center'};
  vertical-align: middle;
  border-right: ${({ noBorder }) =>
    noBorder ? 'none' : `1px solid ${theme.colors.tableBorder}`};
  border-bottom: 1px solid ${theme.colors.tableBorder};
  line-height: 1.2;
  background: ${({ color }) =>
    color === 'primary'
      ? theme.colors.th.primary
      : color === 'secondary'
      ? theme.colors.th.secondary
      : color === 'darkest'
      ? theme.colors.th.darkest
      : color === 'accentYellow'
      ? theme.colors.th.accentYellow
      : color === 'holiday'
      ? theme.colors.th.accentYellow
      : color === 'tertiary'
      ? theme.colors.th.tertiary
      : color === 'quaternary'
      ? theme.colors.th.quaternary
      : theme.colors.th.default};

  @media screen and (max-width: 1047px) {
    padding: 10px 12px 12px 12px;
    font-size: 15px;
  }

  > tr > th[rowspan] + th {
    position: sticky;
    left: ${({ firstHeadWidth }) => firstHeadWidth};
  }

  font-weight: ${({ isBold }) => (isBold ? 'bold' : 'normal')}; // フォントの太さを設定
`

export const Td = styled.td<{
  color?: Color
  textAlign?: TextAlign
  noBorder?: boolean
}>`
  padding: 13px 5px 15px 5px;
  font-size: 16px;
  line-height: 1.2;
  text-align: ${({ textAlign }) => textAlign ?? 'right'};
  background: ${({ color }) =>
    color === 'primary'
      ? theme.colors.th.primary
      : color === 'secondary'
      ? theme.colors.th.secondary
      : theme.colors.td.default};
  vertical-align: middle;
  border-right: ${({ noBorder }) =>
    noBorder ? 'none' : `1px solid ${theme.colors.tableBorder}`};
  border-bottom: 1px solid ${theme.colors.tableBorder};

  @media screen and (max-width: 1047px) {
    padding: 10px 12px 12px 12px;
    font-size: 15px;
  }
`